import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Building2, Package, Calculator, Save, MapPin,
  DollarSign, Percent, Search, ChevronLeft, ChevronRight,
  ArrowUpDown, SortAsc, SortDesc
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { MoneyInput } from '../../components/ui/money-input';
import { UnitInput } from '../../components/ui/unit-input';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ProjectFormData, PriceList } from '../../types';
import { functionalUnitService, FunctionalUnit } from '../../services/functionalUnitService';
import { priceListService } from '../../services/priceListService';
import { projectService } from '../../services/projectService';
import { formatMoney } from '../../utils/format';
import LoadingScreen from '../../components/ui/LoadingScreen';
import { showSuccess, showWarning } from '../../components/Alerts';
import { handleApiError } from '../../utils/errorHandler';
import { useAuth } from '../../context/AuthContext';
import { PERMISSIONS } from '../../constants/permissions';
import RequirePermission from '../../components/RequirePermission';

type SortColumn = 'unit' | 'type' | 'location' | 'covered' | 'total' | 'cashPrice' | 'financedPrice';

const UFS_PER_PAGE = 9;

const ProjectPriceDetail: React.FC = () => {
  const { hasPermission } = useAuth();
  const canEditPriceList = hasPermission(PERMISSIONS.COMERCIAL.PRECIOS_EDITAR);

  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [project, setProject] = useState<ProjectFormData | null>(null);
  const [functionalUnits, setFunctionalUnits] = useState<FunctionalUnit[]>([]);
  const [priceList, setPriceList] = useState<PriceList | null>(null);
  const [priceValues, setPriceValues] = useState({
    cubierto: null as number | null,
    librePercent: null as number | null,
    semiCubiertoPercent: null as number | null,
    bauleraPercent: null as number | null,
    financiadoPercent: null as number | null,
  });
  const [savedValues, setSavedValues] = useState({
    cubierto: null as number | null,
    librePercent: null as number | null,
    semiCubiertoPercent: null as number | null,
    bauleraPercent: null as number | null,
    financiadoPercent: null as number | null,
    currency: 'USD' as 'USD' | 'ARS',
  });
  const [sortColumn, setSortColumn] = useState<SortColumn>('unit');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [selectedCurrency, setSelectedCurrency] = useState<'USD' | 'ARS'>('USD');
  const [ufSearch, setUfSearch] = useState('');
  const [ufPage, setUfPage] = useState(1);

  // Validation states
  const [cubiertoInvalid, setCubiertoInvalid] = useState(false);
  const [libreInvalid, setLibreInvalid] = useState(false);
  const [semiCubiertoInvalid, setSemiCubiertoInvalid] = useState(false);
  const [bauleraInvalid, setBauleraInvalid] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!projectId) return;
      setLoading(true);
      try {
        const projectData = await projectService.getById(parseInt(projectId));
        setProject(projectData);
        const unitsData = await functionalUnitService.getAllFunctionalUnits(parseInt(projectId));
        setFunctionalUnits(unitsData);
        try {
          const priceListData = await priceListService.getPriceListByProjectId(parseInt(projectId));
          setPriceList(priceListData);
          const currency = (priceListData.currency as 'USD' | 'ARS') || 'USD';
          const loaded = {
            cubierto: priceListData.coveredM2,
            librePercent: priceListData.freePercentage,
            semiCubiertoPercent: priceListData.semiCoveredPercentage,
            bauleraPercent: priceListData.bauleraPercentage,
            financiadoPercent: priceListData.financedPercentage,
          };
          setPriceValues(loaded);
          setSavedValues({ ...loaded, currency });
          setSelectedCurrency(currency);
        } catch {
          // no price list yet
        }
      } catch (error) {
        handleApiError(error, 'Error al cargar los datos del proyecto');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [projectId]);

  const handlePriceChange = (field: keyof typeof priceValues, value: number | null) => {
    if (field === 'cubierto' && value != null) setCubiertoInvalid(false);
    if (field === 'librePercent' && value != null) setLibreInvalid(false);
    if (field === 'semiCubiertoPercent' && value != null) setSemiCubiertoInvalid(false);
    if (field === 'bauleraPercent' && value != null) setBauleraInvalid(false);
    setPriceValues(prev => ({ ...prev, [field]: value }));
  };

  const getNumericDisplayValue = (value: number | null | undefined) =>
    value === 0 || value == null ? undefined : value;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSave();
  };

  const handleSave = async () => {
    if (!project) return;
    if (priceValues.cubierto === null || priceValues.cubierto <= 0) {
      setCubiertoInvalid(true);
      showWarning('Campo obligatorio', 'Ingresa el valor m² cubierto');
      document.getElementById('cubierto')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      document.getElementById('cubierto')?.focus();
      return;
    }
    if (priceValues.librePercent === null) {
      setLibreInvalid(true);
      showWarning('Campo obligatorio', 'Ingresa el porcentaje de superficie libre');
      document.getElementById('librePercent')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (priceValues.semiCubiertoPercent === null) {
      setSemiCubiertoInvalid(true);
      showWarning('Campo obligatorio', 'Ingresa el porcentaje de superficie semi-cubierta');
      document.getElementById('semiCubiertoPercent')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (priceValues.bauleraPercent === null) {
      setBauleraInvalid(true);
      showWarning('Campo obligatorio', 'Ingresa el porcentaje de baulera');
      document.getElementById('bauleraPercent')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSaving(true);
    try {
      const priceData = {
        coveredM2: priceValues.cubierto,
        freePercentage: priceValues.librePercent,
        semiCoveredPercentage: priceValues.semiCubiertoPercent,
        bauleraPercentage: priceValues.bauleraPercent,
        financedPercentage: priceValues.financiadoPercent ?? 0,
        currency: selectedCurrency,
      };
      if (priceList) {
        await priceListService.updatePriceList(priceList.id, priceData);
      } else {
        const created = await priceListService.createPriceList({ ...priceData, projectId: project.id! });
        setPriceList(created);
      }
      setSavedValues({ ...priceValues, currency: selectedCurrency });
      showSuccess('Lista de precios guardada exitosamente');
    } catch (error) {
      handleApiError(error, 'Error al guardar la lista de precios');
    } finally {
      setSaving(false);
    }
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setUfPage(1);
  };

  // Reset page when search changes
  useEffect(() => { setUfPage(1); }, [ufSearch, sortColumn, sortDirection]);

  const precioCubierto = priceValues.cubierto || 0;
  const precioLibre = precioCubierto * ((priceValues.librePercent || 0) / 100);
  const precioSemiCubierto = precioCubierto * ((priceValues.semiCubiertoPercent || 0) / 100);
  const precioBaulera = precioCubierto * ((priceValues.bauleraPercent || 0) / 100);
  const porcentajeFinanciamiento = priceValues.financiadoPercent || 0;

  const getSortedFilteredUnits = () => {
    const filtered = functionalUnits.filter(u => {
      if (!ufSearch.trim()) return true;
      const q = ufSearch.toLowerCase();
      return (
        `${u.floor}${u.letter}`.toLowerCase().includes(q) ||
        (u.ufType?.name || '').toLowerCase().includes(q) ||
        (u.ufLocation?.name || '').toLowerCase().includes(q)
      );
    });

    return filtered.sort((a, b) => {
      let cmp = 0;
      switch (sortColumn) {
        case 'unit':
          cmp = `${a.floor}${a.letter}`.localeCompare(`${b.floor}${b.letter}`);
          break;
        case 'type':
          cmp = (a.ufType?.name || '').localeCompare(b.ufType?.name || '');
          break;
        case 'location':
          cmp = (a.ufLocation?.name || '').localeCompare(b.ufLocation?.name || '');
          break;
        case 'covered':
          cmp = a.coveredArea - b.coveredArea;
          break;
        case 'total':
          cmp = (a.coveredArea + a.semiCoveredArea + a.storageArea + a.freeArea)
            - (b.coveredArea + b.semiCoveredArea + b.storageArea + b.freeArea);
          break;
        case 'cashPrice': {
          const pa = (a.coveredArea * precioCubierto) + (a.semiCoveredArea * precioSemiCubierto) + (a.storageArea * precioBaulera) + (a.freeArea * precioLibre);
          const pb = (b.coveredArea * precioCubierto) + (b.semiCoveredArea * precioSemiCubierto) + (b.storageArea * precioBaulera) + (b.freeArea * precioLibre);
          cmp = pa - pb;
          break;
        }
        case 'financedPrice': {
          const fa = ((a.coveredArea * precioCubierto) + (a.semiCoveredArea * precioSemiCubierto) + (a.storageArea * precioBaulera) + (a.freeArea * precioLibre)) * (1 + porcentajeFinanciamiento / 100);
          const fb = ((b.coveredArea * precioCubierto) + (b.semiCoveredArea * precioSemiCubierto) + (b.storageArea * precioBaulera) + (b.freeArea * precioLibre)) * (1 + porcentajeFinanciamiento / 100);
          cmp = fa - fb;
          break;
        }
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  };

  if (loading) return <LoadingScreen message="Cargando lista de precios" />;
  if (saving) return <LoadingScreen message="Guardando lista de precios" />;

  if (!project) {
    return (
      <div className="w-full h-full p-6">
        <div className="p-6 text-center border rounded-lg shadow-sm bg-card border-border">
          <p className="text-muted-foreground">No se encontró el proyecto</p>
          <Button onClick={() => navigate('/pricelists')} className="mt-4">Volver</Button>
        </div>
      </div>
    );
  }

  const hasChanges =
    priceValues.cubierto !== savedValues.cubierto ||
    priceValues.librePercent !== savedValues.librePercent ||
    priceValues.semiCubiertoPercent !== savedValues.semiCubiertoPercent ||
    priceValues.bauleraPercent !== savedValues.bauleraPercent ||
    priceValues.financiadoPercent !== savedValues.financiadoPercent ||
    selectedCurrency !== savedValues.currency;

  const sortedUnits = getSortedFilteredUnits();
  const totalPages = Math.ceil(sortedUnits.length / UFS_PER_PAGE);
  const safePage = Math.min(ufPage, Math.max(1, totalPages));
  const pageUnits = sortedUnits.slice((safePage - 1) * UFS_PER_PAGE, safePage * UFS_PER_PAGE);

  const SortButton = ({ col, label }: { col: SortColumn; label: string }) => {
    const active = sortColumn === col;
    const Icon = active ? (sortDirection === 'asc' ? SortAsc : SortDesc) : ArrowUpDown;
    return (
      <button
        type="button"
        onClick={() => handleSort(col)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border
          ${active
            ? 'bg-primary text-primary-foreground border-primary'
            : 'bg-background text-muted-foreground border-border hover:bg-muted hover:text-foreground'
          }`}
      >
        {label}
        <Icon className="w-3 h-3" />
      </button>
    );
  };

  const derivedPrices = [
    { label: 'Cubierto', value: precioCubierto, highlight: true },
    { label: 'Libre', value: precioLibre },
    { label: 'Semi-cubierto', value: precioSemiCubierto },
    { label: 'Baulera', value: precioBaulera },
  ];

  return (
    <div className="w-full h-full p-4 space-y-6 sm:p-6">

      {/* ── Card principal: formulario ── */}
      <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">

        {/* Header */}
        <div className="px-4 py-5 border-b sm:px-6 border-border">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div className="flex items-center gap-4">
              <Button variant="outline" onClick={() => navigate('/pricelists')} className="flex items-center gap-2 rounded-lg">
                <ArrowLeft className="w-4 h-4" />
                Volver
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Lista de Precios</h1>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{project.name}</span>
                  {project.city && (
                    <>
                      <span className="text-muted-foreground/40">·</span>
                      <MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">{project.city}</span>
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border
              ${priceList ? 'bg-success/10 text-success border-success/20' : 'bg-amber-50 text-amber-600 border-amber-200 dark:bg-amber-900/10 dark:text-amber-400 dark:border-amber-800/30'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${priceList ? 'bg-success' : 'bg-amber-500'}`} />
              {priceList ? 'Lista configurada' : 'Sin configurar'}
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-4 py-6 space-y-8 sm:px-6">

          {/* ── Sección 1: Precio Base ── */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
                <div className="p-1.5 rounded-lg bg-primary/10">
                  <DollarSign className="w-4 h-4 text-primary" />
                </div>
                Precio Base
              </h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                Moneda y valor de referencia por m² cubierto. Todos los demás precios se derivan de este valor.
              </p>
            </div>
            <div className="space-y-4 lg:col-span-2">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="currency" className="text-xs font-medium text-foreground sm:text-sm">Moneda</Label>
                  <Select value={selectedCurrency} onValueChange={(v: 'USD' | 'ARS') => setSelectedCurrency(v)} disabled={!canEditPriceList}>
                    <SelectTrigger id="currency" className="rounded-lg bg-background border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD — Dólar</SelectItem>
                      <SelectItem value="ARS">ARS — Peso Argentino</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cubierto" className="text-xs font-medium text-foreground sm:text-sm">
                    Valor m² Cubierto
                    <span className="ml-1 text-xs text-red-500">*</span>
                  </Label>
                  <MoneyInput
                    id="cubierto"
                    currency={selectedCurrency}
                    placeholder="0,00"
                    value={getNumericDisplayValue(priceValues.cubierto)}
                    onValueChange={(v) => handlePriceChange('cubierto', v)}
                    disabled={!canEditPriceList}
                    className={cubiertoInvalid ? 'border-red-500 border-2' : ''}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-border" />

          {/* ── Sección 2: Porcentajes ── */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-1">
              <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
                <div className="p-1.5 rounded-lg bg-primary/10">
                  <Percent className="w-4 h-4 text-primary" />
                </div>
                Porcentajes
              </h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                Cada porcentaje se aplica sobre el m² cubierto. El financiamiento se aplica sobre el precio total de contado.
              </p>
            </div>
            <div className="space-y-5 lg:col-span-2">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="librePercent" className="text-xs font-medium text-foreground sm:text-sm">
                    % Superficie Libre
                    <span className="ml-1 text-xs text-red-500">*</span>
                  </Label>
                  <UnitInput
                    id="librePercent"
                    unit="%"
                    placeholder="0,00"
                    value={getNumericDisplayValue(priceValues.librePercent)}
                    onValueChange={(v) => handlePriceChange('librePercent', v)}
                    disabled={!canEditPriceList}
                    className={libreInvalid ? 'border-red-500 border-2' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="semiCubiertoPercent" className="text-xs font-medium text-foreground sm:text-sm">
                    % Semi-cubierto
                    <span className="ml-1 text-xs text-red-500">*</span>
                  </Label>
                  <UnitInput
                    id="semiCubiertoPercent"
                    unit="%"
                    placeholder="0,00"
                    value={getNumericDisplayValue(priceValues.semiCubiertoPercent)}
                    onValueChange={(v) => handlePriceChange('semiCubiertoPercent', v)}
                    disabled={!canEditPriceList}
                    className={semiCubiertoInvalid ? 'border-red-500 border-2' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bauleraPercent" className="text-xs font-medium text-foreground sm:text-sm">
                    % Baulera
                    <span className="ml-1 text-xs text-red-500">*</span>
                  </Label>
                  <UnitInput
                    id="bauleraPercent"
                    unit="%"
                    placeholder="0,00"
                    value={getNumericDisplayValue(priceValues.bauleraPercent)}
                    onValueChange={(v) => handlePriceChange('bauleraPercent', v)}
                    disabled={!canEditPriceList}
                    className={bauleraInvalid ? 'border-red-500 border-2' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="financiadoPercent" className="text-xs font-medium text-foreground sm:text-sm">% Financiamiento</Label>
                  <UnitInput
                    id="financiadoPercent"
                    unit="%"
                    placeholder="0,00"
                    value={getNumericDisplayValue(priceValues.financiadoPercent)}
                    onValueChange={(v) => handlePriceChange('financiadoPercent', v)}
                    disabled={!canEditPriceList}
                  />
                </div>
              </div>

              {/* Resumen de precios derivados */}
              {precioCubierto > 0 && (
                <div className="p-4 border rounded-xl bg-primary/5 border-primary/20">
                  <div className="flex items-center gap-2 mb-3">
                    <Calculator className="w-4 h-4 text-primary" />
                    <p className="text-sm font-semibold text-foreground">Precios por m² calculados</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {derivedPrices.map(({ label, value, highlight }) => (
                      <div key={label} className={`rounded-lg p-3 ${highlight ? 'bg-primary/10 border border-primary/20' : 'bg-background border border-border'}`}>
                        <p className="text-xs text-muted-foreground mb-1">{label}</p>
                        <p className={`text-sm font-bold font-mono leading-tight ${highlight ? 'text-primary' : 'text-foreground'}`}>
                          {selectedCurrency} {formatMoney(value)}
                        </p>
                      </div>
                    ))}
                  </div>
                  {porcentajeFinanciamiento > 0 && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      Recargo por financiamiento: <span className="font-semibold text-foreground">+{porcentajeFinanciamiento}%</span> sobre precio contado
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Acciones */}
          <RequirePermission permission={PERMISSIONS.COMERCIAL.PRECIOS_EDITAR}>
            <div className="flex flex-col-reverse gap-3 pt-4 border-t sm:flex-row sm:justify-end border-border">
              <Button type="button" variant="outline" className="w-full rounded-lg sm:w-auto" onClick={() => navigate('/pricelists')}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saving || !hasChanges} className="w-full rounded-lg bg-primary text-primary-foreground hover:opacity-90 sm:w-auto">
                <Save className="w-4 h-4 mr-2" />
                {priceList ? 'Guardar Cambios' : 'Crear Lista de Precios'}
              </Button>
            </div>
          </RequirePermission>
        </form>
      </div>

      {/* ── Card secundaria: Unidades Funcionales ── */}
      <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">

        {/* Header de la sección */}
        <div className="px-4 py-4 border-b sm:px-6 border-border">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <Package className="w-5 h-5 text-primary" />
                Unidades Funcionales
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                {functionalUnits.length > 0
                  ? `${sortedUnits.length} de ${functionalUnits.length} unidad${functionalUnits.length !== 1 ? 'es' : ''}`
                  : 'Vista previa de precios por unidad'}
              </p>
            </div>
            {functionalUnits.length > 0 && (
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder="Buscar por unidad, tipo..."
                  value={ufSearch}
                  onChange={e => setUfSearch(e.target.value)}
                  className="pl-9 rounded-lg focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
            )}
          </div>

          {/* Sort controls */}
          {functionalUnits.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mt-4">
              <span className="text-xs text-muted-foreground font-medium mr-1">Ordenar:</span>
              <SortButton col="unit" label="Unidad" />
              <SortButton col="type" label="Tipo" />
              <SortButton col="location" label="Ubicación" />
              <SortButton col="covered" label="Cubierto" />
              <SortButton col="total" label="Total m²" />
              <SortButton col="cashPrice" label="Precio contado" />
              <SortButton col="financedPrice" label="Precio financiado" />
            </div>
          )}
        </div>

        {/* Grid de cards */}
        <div className="p-4 sm:p-6">
          {functionalUnits.length === 0 ? (
            <div className="py-16 text-center">
              <div className="mx-auto mb-5 w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Package className="w-8 h-8 text-primary" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-foreground">Sin unidades funcionales</h3>
              <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                Las unidades funcionales se crean desde el módulo de proyectos.
              </p>
            </div>
          ) : sortedUnits.length === 0 ? (
            <div className="py-12 text-center">
              <Search className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No hay unidades que coincidan con la búsqueda.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {pageUnits.map((unit) => {
                  const coveredTotal = unit.coveredArea * precioCubierto;
                  const semiCoveredTotal = unit.semiCoveredArea * precioSemiCubierto;
                  const storageTotal = unit.storageArea * precioBaulera;
                  const freeTotal = unit.freeArea * precioLibre;
                  const unitTotal = coveredTotal + semiCoveredTotal + storageTotal + freeTotal;
                  const totalM2 = unit.coveredArea + unit.semiCoveredArea + unit.storageArea + unit.freeArea;
                  const precioFinanciado = unitTotal * (1 + porcentajeFinanciamiento / 100);

                  const surfaces = [
                    { label: 'Cubierto', area: unit.coveredArea, subtotal: coveredTotal },
                    { label: 'Semi-cub.', area: unit.semiCoveredArea, subtotal: semiCoveredTotal },
                    { label: 'Baulera', area: unit.storageArea, subtotal: storageTotal },
                    { label: 'Libre', area: unit.freeArea, subtotal: freeTotal },
                  ].filter(s => s.area > 0);

                  return (
                    <div
                      key={`${unit.floor}-${unit.letter}`}
                      className="flex flex-col bg-card border border-border rounded-xl overflow-hidden transition-all hover:shadow-md hover:border-primary/30"
                    >
                      {/* Card header */}
                      <div className="flex items-center gap-3 px-4 py-3 bg-muted/30">
                        <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 text-primary font-bold text-sm flex-shrink-0">
                          {unit.letter}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-foreground text-sm truncate">
                            Piso {unit.floor} · Unidad {unit.letter}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {unit.ufType?.name || 'Sin tipo'}
                          </p>
                        </div>
                        {unit.ufLocation?.name && (
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <MapPin className="w-3 h-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">{unit.ufLocation.name}</span>
                          </div>
                        )}
                      </div>

                      {/* Superficies */}
                      {surfaces.length > 0 && (
                        <div className="px-4 py-3 border-t border-border">
                          <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
                            {surfaces.map(s => (
                              <div key={s.label} className="space-y-0.5">
                                <p className="text-xs text-muted-foreground">{s.label}</p>
                                <p className="text-sm font-medium text-foreground">{s.area.toFixed(2)} m²</p>
                                {precioCubierto > 0 && (
                                  <p className="text-xs text-muted-foreground font-mono">
                                    {selectedCurrency} {formatMoney(s.subtotal)}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Total m² */}
                      <div className="px-4 py-2.5 border-t border-border mt-auto">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Total superficie</span>
                          <span className="font-semibold text-foreground">{totalM2.toFixed(2)} m²</span>
                        </div>
                      </div>

                      {/* Precios footer */}
                      <div className="grid grid-cols-2 border-t border-border">
                        <div className="px-4 py-3 bg-success/8">
                          <p className="text-xs text-muted-foreground mb-1">Contado</p>
                          <p className="font-bold text-success text-sm font-mono leading-tight">
                            {selectedCurrency} {formatMoney(unitTotal)}
                          </p>
                        </div>
                        <div className="px-4 py-3 bg-primary/5 border-l border-border">
                          <p className="text-xs text-muted-foreground mb-1">Financiado</p>
                          <p className="font-bold text-primary text-sm font-mono leading-tight">
                            {selectedCurrency} {formatMoney(precioFinanciado)}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Paginación */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between pt-6 mt-6 border-t border-border">
                  <p className="text-sm text-muted-foreground">
                    Mostrando{' '}
                    <span className="font-semibold text-foreground">{(safePage - 1) * UFS_PER_PAGE + 1}–{Math.min(safePage * UFS_PER_PAGE, sortedUnits.length)}</span>
                    {' '}de{' '}
                    <span className="font-semibold text-foreground">{sortedUnits.length}</span> unidades
                  </p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setUfPage(p => p - 1)} disabled={safePage === 1}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm font-medium text-foreground px-1">{safePage} / {totalPages}</span>
                    <Button variant="outline" size="sm" onClick={() => setUfPage(p => p + 1)} disabled={safePage === totalPages}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectPriceDetail;
